import { Skeleton, Table } from '@mantine/core';
import { useHorizontalOverflow } from '@xver/hooks/useHorizontalOverflow';
import { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { tableStyles } from './tableStyles';

export interface DataTableColumn<T> {
    key: string;
    label: ReactNode;
    render: (row: T) => ReactNode;
    width?: string | number;
    align?: 'left' | 'right' | 'center';
}

export interface DataTableProps<T> {
    columns: DataTableColumn<T>[];
    rows: T[] | undefined;
    isLoading?: boolean;
    onRowClick?: (row: T) => void;
    isRowClickable?: (row: T) => boolean;
    actions?: (row: T) => ReactNode;
    emptyMessage?: ReactNode;
    skeletonRows?: number;
    rowKey: (row: T) => string | number;
    className?: string;
    /**
     * Visually hidden table caption. Also names the horizontal scroll region, which becomes
     * keyboard-focusable only while the table overflows, so every table needs one.
     */
    caption: string;
    /** Visually hidden header text for the `actions` column; defaults to "Actions". */
    actionsLabel?: string;
}

export default function DataTable<T>({
    columns,
    rows,
    isLoading,
    onRowClick,
    isRowClickable,
    actions,
    emptyMessage = 'No items found.',
    skeletonRows = 5,
    rowKey,
    className,
    caption,
    actionsLabel,
}: DataTableProps<T>) {
    const { t } = useTranslation('projects');
    const scrollRef = useRef<HTMLDivElement>(null);
    const overflowing = useHorizontalOverflow(scrollRef);
    const safeRows = rows ?? [];
    const hasRows = safeRows.length > 0;
    const showSkeleton = isLoading && safeRows.length === 0;
    const colSpan = columns.length + (actions ? 1 : 0);

    return (
        <div
            ref={scrollRef}
            role="region"
            aria-label={caption}
            tabIndex={overflowing ? 0 : undefined}
            className="relative w-full rounded-md overflow-x-auto overflow-y-hidden max-w-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg"
        >
            <Table
                highlightOnHover
                className={className}
                styles={tableStyles}
                aria-busy={showSkeleton || undefined}
            >
                <Table.Caption className="sr-only">{caption}</Table.Caption>
                <Table.Thead>
                    <Table.Tr>
                        {columns.map((col) => (
                            <Table.Th
                                key={col.key}
                                scope="col"
                                style={{
                                    width: col.width,
                                    textAlign: col.align,
                                }}
                            >
                                {col.label}
                            </Table.Th>
                        ))}
                        {actions && (
                            <Table.Th scope="col">
                                <span className="sr-only">
                                    {actionsLabel ?? t('common.actions')}
                                </span>
                            </Table.Th>
                        )}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {showSkeleton ? (
                        Array.from({ length: skeletonRows }, (_, index) => (
                            <Table.Tr key={`skeleton-${index}`} aria-hidden="true">
                                {columns.map((col) => (
                                    <Table.Td key={col.key}>
                                        <Skeleton height={16} width="60%" radius="xs" />
                                    </Table.Td>
                                ))}
                                {actions && (
                                    <Table.Td>
                                        <div className="flex justify-end">
                                            <Skeleton height={16} width={30} radius="xs" />
                                        </div>
                                    </Table.Td>
                                )}
                            </Table.Tr>
                        ))
                    ) : hasRows ? (
                        safeRows.map((row) => {
                            const clickable = !!onRowClick && (isRowClickable?.(row) ?? true);
                            return (
                                <Table.Tr
                                    key={rowKey(row)}
                                    className={onRowClick && !clickable ? 'no-hover' : undefined}
                                    onClick={clickable ? () => onRowClick(row) : undefined}
                                    style={clickable ? { cursor: 'pointer' } : undefined}
                                >
                                    {columns.map((col) => (
                                        <Table.Td key={col.key} style={{ textAlign: col.align }}>
                                            {col.render(row)}
                                        </Table.Td>
                                    ))}
                                    {actions && <Table.Td>{actions(row)}</Table.Td>}
                                </Table.Tr>
                            );
                        })
                    ) : (
                        <Table.Tr>
                            <Table.Td colSpan={colSpan} className="text-center py-4">
                                {emptyMessage}
                            </Table.Td>
                        </Table.Tr>
                    )}
                </Table.Tbody>
            </Table>
        </div>
    );
}
